<template>
  <div class="client-releases-page">
    <div class="releases-shell">
      <header class="releases-hero">
        <router-link :to="backPath" class="back-btn">
          <i class="el-icon-arrow-left"></i>
          {{ backLabel }}
        </router-link>
        <h1>{{ $tt("客户端版本与更新记录") }}</h1>
        <p class="hero-lead">
          {{ $tt("选择系统与架构，查看历史版本说明并下载安装包，无需登录。") }}
        </p>
      </header>

      <section class="filter-card">
        <div class="platform-tabs">
          <button
            v-for="col in platformColumns"
            :key="col.key"
            type="button"
            class="platform-tab"
            :class="{ active: activeTab === col.key }"
            @click="selectTab(col.key)"
          >
            <span class="tab-icon-shell" :class="'tab-icon-shell--' + col.key">
              <img
                v-if="resolveDownloadIcon(col)"
                :src="resolveDownloadIcon(col)"
                alt=""
                class="tab-icon"
              />
              <i v-else :class="['tab-fallback-icon', col.fallbackIcon]"></i>
            </span>
            <span class="tab-title">{{ $tt(col.title) }}</span>
          </button>
        </div>

        <div v-if="showArchBar" class="arch-row">
          <span class="arch-label">{{ $tt("CPU 架构") }}</span>
          <div class="arch-chips">
            <button
              v-for="o in archChoices"
              :key="o.value"
              type="button"
              class="arch-chip"
              :class="{ active: selectedArch === o.value }"
              @click="selectArch(o.value)"
            >
              {{ $tt(o.label) }}
            </button>
          </div>
        </div>
      </section>

      <section v-loading="loading" class="releases-panel">
        <div v-if="!loading && !versions.length" class="releases-empty">
          <div class="empty-icon"><i class="el-icon-box"></i></div>
          <p class="empty-title">{{ $tt("暂无安装包，敬请期待") }}</p>
          <p class="empty-hint">{{ $tt("管理员尚未在客户端管理中上传该端安装包。") }}</p>
        </div>

        <article
          v-for="(item, index) in versions"
          :key="item.id"
          class="release-card"
          :class="{ 'release-card--featured': index === 0 }"
        >
          <div class="release-accent" aria-hidden="true"></div>
          <div class="release-inner">
            <div class="release-head">
              <div class="release-title-block">
                <div class="ver-row">
                  <span class="ver-badge">v{{ item.name || "—" }}</span>
                  <el-tag v-if="index === 0" size="mini" type="warning" effect="plain">{{ $tt("最新") }}</el-tag>
                  <el-tag v-if="item.status !== 'true'" size="mini" type="info" effect="plain">{{ $tt("不参与 OTA") }}</el-tag>
                </div>
                <p class="ver-meta">
                  <i class="el-icon-time"></i>
                  {{ formatTime(item.create_time) }}
                </p>
              </div>
              <button
                type="button"
                class="download-btn"
                :disabled="!item.file_down_path && !downloadUrls[item.id]"
                @click="downloadItem(item)"
              >
                <i class="el-icon-download"></i>
                {{ $tt("下载") }}
              </button>
            </div>

            <div v-if="item.version_log" class="release-block">
              <h3>{{ $tt("更新内容") }}</h3>
              <div class="release-text">{{ item.version_log }}</div>
            </div>
            <div v-if="item.remark" class="release-block">
              <h3>{{ $tt("版本说明") }}</h3>
              <div class="release-text">{{ item.remark }}</div>
            </div>
            <p v-if="!item.version_log && !item.remark" class="release-muted">{{ $tt("暂无文字说明") }}</p>
          </div>
        </article>
      </section>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { resolveDownloadIcon } from "@/utils/downloadIcons";
import {
  ARCH_OPTIONS,
  CLIENT_DOWNLOAD_ORDER,
  needsArchPicker,
  defaultArch,
} from "@/utils/clientPlatforms";
import { triggerClientDownload } from "@/utils/clientDownload";

export default {
  name: "ClientReleases",
  data() {
    return {
      activeTab: "android",
      selectedArch: defaultArch("android"),
      versions: [],
      loading: false,
      downloadUrls: {},
      welcomeList: [],
    };
  },
  computed: {
    backPath() {
      const from = this.$route.query && this.$route.query.from;
      return from === "home" ? "/homePage" : "/login";
    },
    backLabel() {
      const from = this.$route.query && this.$route.query.from;
      return from === "home" ? this.$tt("返回首页") : this.$tt("返回登录");
    },
    platformColumns() {
      const byName = {};
      const byId = {};
      (this.welcomeList || []).forEach((item) => {
        const name = item.name || item.title;
        const id = String(item.id || "");
        if (name) byName[name] = item;
        if (id) byId[id] = item;
      });
      return CLIENT_DOWNLOAD_ORDER.map((spec) => {
        const remote = byName[spec.matchName] || byId[spec.welcomeId] || {};
        return {
          ...spec,
          name: remote.name || spec.matchName,
          icon: remote.icon || "",
        };
      });
    },
    showArchBar() {
      return needsArchPicker(this.activeTab);
    },
    archChoices() {
      if (this.activeTab === "android") return ARCH_OPTIONS.android;
      if (this.activeTab === "kylin") return ARCH_OPTIONS.qt_kylin;
      if (this.activeTab === "mac") return ARCH_OPTIONS.qt_mac;
      if (this.activeTab === "ios") return ARCH_OPTIONS.qt_ios;
      return [];
    },
  },
  watch: {
    "$route.query": {
      handler(q) {
        if (!q) return;
        const tabs = ["android", "ios", "windows", "kylin", "mac"];
        if (tabs.includes(q.tab) && q.tab !== this.activeTab) {
          this.activeTab = q.tab;
        }
        if (q.arch && q.arch !== this.selectedArch) {
          this.selectedArch = q.arch;
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.applyRouteQuery();
    this.loadWelcomeIcons();
    this.loadCatalog();
  },
  methods: {
    resolveDownloadIcon,
    applyRouteQuery() {
      const q = this.$route.query || {};
      const tabs = ["android", "ios", "windows", "kylin", "mac"];
      if (tabs.includes(q.tab)) {
        this.activeTab = q.tab;
      }
      if (needsArchPicker(this.activeTab)) {
        this.selectedArch = q.arch || defaultArch(this.activeTab);
      }
    },
    loadWelcomeIcons() {
      this.$request
        .getDownloadList({})
        .then((res) => {
          if (res.status === "success") {
            this.welcomeList = res.data || [];
          }
        })
        .catch(() => {});
    },
    syncRouteQuery() {
      const query = Object.assign({}, this.$route.query, { tab: this.activeTab });
      if (needsArchPicker(this.activeTab)) {
        query.arch = this.selectedArch;
      } else {
        delete query.arch;
      }
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ path: this.$route.path, query }).catch(() => {});
      }
    },
    selectTab(key) {
      if (this.activeTab === key) return;
      this.activeTab = key;
      if (needsArchPicker(key)) {
        const qArch = this.$route.query && this.$route.query.arch;
        const valid = this.archChoices.some((o) => o.value === qArch);
        this.selectedArch = valid ? qArch : defaultArch(key);
      }
      this.syncRouteQuery();
      this.loadCatalog();
    },
    selectArch(value) {
      if (this.selectedArch === value) return;
      this.selectedArch = value;
      this.syncRouteQuery();
      this.loadCatalog();
    },
    formatTime(t) {
      if (!t) return "—";
      return moment(t).format("YYYY-MM-DD HH:mm");
    },
    catalogPayload() {
      const payload = { client_type: this.activeTab };
      if (needsArchPicker(this.activeTab)) {
        payload.cpu_arch = this.selectedArch;
      }
      return payload;
    },
    async loadCatalog() {
      this.loading = true;
      this.versions = [];
      try {
        const res = await this.$request.listClientReleaseCatalog(this.catalogPayload());
        if (res.status === "success" && res.data) {
          this.versions = res.data.versions || [];
        }
      } finally {
        this.loading = false;
      }
    },
    async downloadItem(item) {
      let href = item.file_down_path || this.downloadUrls[item.id];
      if (!href) {
        const res = await this.$request.resolveClientDownload({
          client_type: this.activeTab,
          version_id: item.id,
          cpu_arch: needsArchPicker(this.activeTab) ? this.selectedArch : undefined,
        });
        if (res.status !== "success" || !res.data || !res.data.href) {
          this.$message.error(res.msg || this.$tt("无法获取下载地址"));
          return;
        }
        href = res.data.href;
        this.$set(this.downloadUrls, item.id, href);
      }
      triggerClientDownload(href, item.file_real_name || item.file_name || "");
    },
  },
};
</script>

<style lang="scss" scoped>
.client-releases-page {
  min-height: 100vh;
  padding: 28px 16px 56px;
  background: linear-gradient(165deg, #e8f0fe 0%, #f4f6f9 38%, #eef1f6 100%);
}

.releases-shell {
  max-width: 920px;
  margin: 0 auto;
}

.releases-hero {
  margin-bottom: 20px;
  text-align: center;

  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 16px;
    padding: 6px 14px;
    font-size: 13px;
    color: #409eff;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid rgba(64, 158, 255, 0.25);
    border-radius: 20px;
    transition: background 0.2s, box-shadow 0.2s;

    &:hover {
      background: #fff;
      box-shadow: 0 4px 12px rgba(64, 158, 255, 0.12);
    }
  }

  h1 {
    margin: 0 0 10px;
    font-size: 26px;
    font-weight: 600;
    color: #1f2d3d;
    letter-spacing: 0.02em;
  }

  .hero-lead {
    margin: 0 auto;
    max-width: 520px;
    font-size: 14px;
    line-height: 1.6;
    color: #606266;
  }
}

.filter-card {
  padding: 18px 18px 16px;
  margin-bottom: 20px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(228, 231, 237, 0.9);
  box-shadow: 0 8px 28px rgba(31, 45, 61, 0.06);
}

.platform-tabs {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.platform-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 88px;
  padding: 12px 6px;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 12px;
  background: #f5f7fa;
  color: #606266;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s, color 0.2s;

  &.active {
    background: #fff;
    border-color: #409eff;
    color: #409eff;
    box-shadow: 0 6px 18px rgba(64, 158, 255, 0.14);
  }

  .tab-title {
    font-size: 13px;
    line-height: 1.25;
    text-align: center;
  }
}

.tab-icon-shell {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  flex-shrink: 0;

  &--android {
    background: #fff7e6;
  }
  &--ios {
    background: #f0f0f0;
  }
  &--windows {
    background: #ecf5ff;
  }
  &--kylin {
    border-radius: 50%;
    background: linear-gradient(145deg, #ecf5ff 0%, #d9ecff 100%);
  }
  &--mac {
    background: #ecf5ff;
  }
}

.tab-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.tab-fallback-icon {
  font-size: 22px;
  color: #409eff;
}

.arch-row {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed #ebeef5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
}

.arch-label {
  font-size: 13px;
  font-weight: 500;
  color: #909399;
  flex-shrink: 0;
}

.arch-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.arch-chip {
  border: 1px solid #dcdfe6;
  background: #f5f7fa;
  border-radius: 18px;
  padding: 7px 16px;
  font-size: 13px;
  cursor: pointer;
  color: #606266;
  transition: all 0.2s;

  &.active {
    border-color: #409eff;
    background: linear-gradient(180deg, #ecf5ff 0%, #d9ecff 100%);
    color: #409eff;
    font-weight: 500;
  }
}

.releases-panel {
  min-height: 160px;
}

.releases-empty {
  padding: 48px 24px;
  text-align: center;
  background: #fff;
  border-radius: 16px;
  border: 1px dashed #dcdfe6;

  .empty-icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 16px;
    border-radius: 50%;
    background: #f5f7fa;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: #c0c4cc;
  }

  .empty-title {
    margin: 0 0 8px;
    font-size: 16px;
    color: #606266;
  }

  .empty-hint {
    margin: 0;
    font-size: 13px;
    color: #909399;
    line-height: 1.5;
  }
}

.release-card {
  position: relative;
  margin-bottom: 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #ebeef5;
  box-shadow: 0 4px 16px rgba(31, 45, 61, 0.05);
  overflow: hidden;

  &--featured {
    border-color: rgba(64, 158, 255, 0.35);
    box-shadow: 0 8px 24px rgba(64, 158, 255, 0.1);
  }
}

.release-accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #409eff 0%, #79bbff 100%);
}

.release-card--featured .release-accent {
  width: 5px;
  background: linear-gradient(180deg, #e6a23c 0%, #409eff 100%);
}

.release-inner {
  padding: 20px 22px 20px 26px;
}

.release-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 4px;
}

.ver-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.ver-badge {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  letter-spacing: -0.02em;
}

.ver-meta {
  margin: 0;
  font-size: 13px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 38px;
  padding: 0 22px;
  border: none;
  border-radius: 19px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(135deg, #409eff 0%, #337ecc 100%);
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.35);
  transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(64, 158, 255, 0.4);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }
}

.release-block {
  margin-top: 14px;

  h3 {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 600;
    color: #909399;
    text-transform: none;
  }
}

.release-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.65;
  color: #303133;
  background: #f8fafc;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #f0f2f5;
}

.release-muted {
  margin: 12px 0 0;
  font-size: 13px;
  color: #c0c4cc;
}

@media (max-width: 720px) {
  .platform-tabs {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .releases-hero h1 {
    font-size: 22px;
  }

  .release-head {
    flex-direction: column;
    align-items: stretch;
  }

  .download-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
